import { memo, SyntheticEvent, useCallback, useMemo } from 'react'
import { Divider } from '@cargomart/ui'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { sumBy } from 'helpers/number'
import { pluralizer } from 'helpers/string'
import { formattedTotalCargoParamsByType } from 'modules/order/helpers'
import { getTotalCargoParams, getTotalCargoPrice } from 'modules/order/helpers/cargo'
import { formatNumber } from 'shared/helpers/formatNumber'
import { OrderCargoInfo } from 'src/schema'

import { OrderSummaryBlockProps } from './types'

export const OrderSummaryBlock = memo<OrderSummaryBlockProps>(function OrderSummaryBlock(props) {
  const {
    className,
    isOrderDetail,
    cargoInfo,
    schedule,
    trackingError,
    isLoading,
    isScheduleLoading,
    onOpenModalSchedule,
    isShowFullInfo = true
  } = props

  const cargoAmount = useMemo((): string => {
    let amount = cargoInfo?.total?.amount

    if (cargoInfo?.type === OrderCargoInfo.type.SEPARATE && cargoInfo.separate) {
      amount = sumBy(cargoInfo.separate, ({ amount }) => amount)
    }

    return amount ? `${formatNumber({ value: amount })} ${pluralizer(amount, 'место', 'места', 'мест')}` : '—'
  }, [cargoInfo])

  const totalCargoPrice = useMemo((): string => {
    const price =
      cargoInfo?.type === OrderCargoInfo.type.SEPARATE && cargoInfo.separate
        ? getTotalCargoPrice(cargoInfo.separate)
        : cargoInfo?.total?.price

    return price ? `${formatNumber({ value: price, precision: 2 })} ₽` : '—'
  }, [cargoInfo])

  const { weight, volume } = getTotalCargoParams({
    cargoType: cargoInfo?.type,
    cargo: cargoInfo?.separate,
    cargoTotal: cargoInfo?.total
  })

  const handleOpenModalSchedule = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()
      onOpenModalSchedule?.()
    },
    [onOpenModalSchedule]
  )

  return (
    <div className={className}>
      {!isOrderDetail && <h2 className="mb-4 text-headline-sm text-on-surface">Параметры груза</h2>}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-body-md text-on-surface">
          {!isLoading ? (
            <>
              <div className="text-start">Всего позиций</div>
              <div className="text-end">{cargoAmount}</div>
            </>
          ) : (
            <PlaceholderCell height={20} />
          )}
        </div>
        <div className="flex justify-between text-body-md text-on-surface">
          {!isLoading ? (
            <>
              <div className="text-start">Общий вес и объём</div>
              <div className="text-end">{formattedTotalCargoParamsByType(cargoInfo?.type, weight, volume)}</div>
            </>
          ) : (
            <PlaceholderCell height={20} />
          )}
        </div>
        <div className="flex justify-between text-body-md text-on-surface">
          {!isLoading ? (
            <>
              <div className="text-start">Объявленная ценность</div>
              <div className="text-end">{totalCargoPrice}</div>
            </>
          ) : (
            <PlaceholderCell height={20} />
          )}
        </div>
        {isOrderDetail && <Divider className="my-2" />}
      </div>
      {isOrderDetail && (
        <div className="mt-2">
          {isLoading || isScheduleLoading ? (
            <PlaceholderCell height={20} />
          ) : (
            <>
              {trackingError || schedule?.length ? (
                <div>
                  <div className="text-body-sm text-on-surface-variant">
                    Отправка производится на&nbsp;следующий рабочий день после приёмки груза.
                  </div>
                  {isShowFullInfo && (
                    <a className="link cursor-pointer text-body-sm" href="#" onClick={handleOpenModalSchedule}>
                      График отправлений машин
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-body-md text-on-negative-container">
                  На&nbsp;данный момент для выбранного направления нет информации о&nbsp;сроках доставки и&nbsp;времени
                  выезда машин.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
})
